import { useCallback, useEffect, useRef } from 'react';
import { FILTER_PARAM } from '../pages/utils/viewConfig';

/**
 * Encapsulates all URL-synchronisation logic for the filter URL param.
 *
 * Responsibilities:
 *  1. Provides a stable `setFilterParam` helper that writes/clears the filter
 *     query param in the URL.
 *  2. Wraps `saveFilter` so the URL is updated only after the new filter entry
 *     has appeared in `savedFilters` — preventing the "not in the list" flash.
 *  3. Strips stale `saved:` params when filters load and the referenced id is gone
 *     (e.g. filter deleted in another tab).
 *
 * @param {object[]} savedFilters - current saved filters array from useSavedFilters
 * @param {boolean}  filtersLoading - true while initial fetch is in progress
 * @param {URLSearchParams} searchParams
 * @param {Function} setSearchParams - React Router setter
 * @param {Function} saveFilter - useSavedFilters.save
 * @returns {{ setFilterParam, handleSaveAsFilter }}
 */
export function useFilterParam(savedFilters, filtersLoading, searchParams, setSearchParams, saveFilter) {
    // Holds the ID of a newly saved filter waiting to become active once it
    // appears in savedFilters. Decouples the URL update from the save call so
    // the left panel always shows the entry before navigation commits.
    const pendingNavigationId = useRef(null);

    const setFilterParam = useCallback((value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(FILTER_PARAM, value); else next.delete(FILTER_PARAM);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    /**
     * Save: fire-and-forget persist, then park the new ID in a ref.
     * Navigation happens in the effect below once savedFilters has committed
     * the new entry — this guarantees the left panel shows the filter before
     * the URL changes to it, eliminating the "not in the list" flash.
     */
    const handleSaveAsFilter = useCallback(({ name, rows }) => {
        const newFilter = saveFilter({ name, rows });
        if (newFilter?.id) {
            pendingNavigationId.current = newFilter.id;
        }
        return newFilter;
    }, [saveFilter]);

    // Navigate to a newly saved filter as soon as it appears in savedFilters.
    useEffect(() => {
        const id = pendingNavigationId.current;
        if (!id) return;
        if (!savedFilters.find((f) => f.id === id)) return;
        pendingNavigationId.current = null;
        setFilterParam(`saved:${id}`);
    }, [savedFilters, setFilterParam]);

    // Strip stale saved: params after filters load (e.g. filter deleted in another tab).
    const filterParam = searchParams.get(FILTER_PARAM);
    useEffect(() => {
        if (filtersLoading) return;
        if (!filterParam?.startsWith('saved:')) return;
        const id = filterParam.slice(6);
        if (savedFilters.find((f) => f.id === id)) return;
        if (pendingNavigationId.current === id) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete(FILTER_PARAM);
            return next;
        }, { replace: true });
    }, [savedFilters, filtersLoading, filterParam, setSearchParams]);

    return { setFilterParam, handleSaveAsFilter };
}
